import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import noti_svg from "./assets/notification.svg";
import defaultAppLogo from "../assets/smarttt-default-logo.png";
import profileService from "./funtionsBD/Mainservice";
import { useBrand } from "../context/BrandContext";
import { resolveClubImageUrl } from "../utils/resolveClubImageUrl";
import { isSuperAdminUser } from "../utils/brandAuth";
import { readActiveHomeRole } from "../utils/homeRole";
import { openSwitchClubDialog } from "../utils/switchActiveClubClient";
import { mergeProfilePreservingActiveClub } from "../utils/mergeProfilePreservingActiveClub";
import { HeaderRoleBadge } from "./HeaderRoleBadge";
import "./Header.css";

/** @returns {number} Positive count, or 0. Supports future profile fields. */
export function getUserNotificationCount(user, overrideCount) {
  if (overrideCount != null) {
    const o = Number(overrideCount);
    if (Number.isFinite(o) && o > 0) return o;
  }
  if (!user || typeof user !== "object") return 0;
  const keys = [
    user.notification_count,
    user.unread_notification_count,
    user.notifications_unread,
    user.unreadNotifications,
  ];
  for (const c of keys) {
    const n = Number(c);
    if (Number.isFinite(n) && n > 0) return n;
  }
  if (Array.isArray(user.notifications) && user.notifications.length > 0) {
    return user.notifications.length;
  }
  return 0;
}

function readUser() {
  try {
    return JSON.parse(localStorage.getItem("user") || "{}");
  } catch {
    return {};
  }
}

export const Header = (props) => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const [userSyncTick, setUserSyncTick] = useState(0);

  useEffect(() => {
    const onUserUpdated = () => setUserSyncTick((n) => n + 1);
    const onStorage = (e) => {
      if (e.key === "user" || e.key == null) onUserUpdated();
    };
    window.addEventListener("smarttt-user-updated", onUserUpdated);
    window.addEventListener("smarttt-club-changed", onUserUpdated);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("smarttt-user-updated", onUserUpdated);
      window.removeEventListener("smarttt-club-changed", onUserUpdated);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  // Re-read when parent passes user or after profile / cross-tab user updates
  void userSyncTick;
  let user;
  try {
    const stored = JSON.parse(localStorage.getItem("user") || "{}");
    user = props.user ? { ...props.user, ...stored } : stored;
  } catch (e) {
    user = {};
  }
  let selectedGroup = {};
  try {
    selectedGroup = JSON.parse(localStorage.getItem("grpinf") || "{}");
  } catch (e) {
    selectedGroup = {};
  }

  const clubImage =
    brand?.logo_url ||
    props.logo ||
    user?.club_details?.club_image ||
    user?.club?.club_image ||
    user?.club_image ||
    selectedGroup?.groupDetails?.club_image ||
    selectedGroup?.groupDetails?.club?.club_image ||
    null;

  const resolvedLogoFromUser = useMemo(() => {
    if (isSuperAdminUser(user)) return brand?.logo_url || null;
    return clubImage;
  }, [
    brand?.logo_url,
    props.logo,
    clubImage,
    user?.isSuperAdmin,
    user?.type_user_id,
  ]);

  const [resolvedLogo, setResolvedLogo] = useState(
    resolveClubImageUrl(resolvedLogoFromUser) || defaultAppLogo
  );
  const fetchedLogoRef = useRef(false);

  const refreshLogoFromProfile = async () => {
    if (isSuperAdminUser(user)) {
      setResolvedLogo(defaultAppLogo);
      return false;
    }
    try {
      const response = await profileService();
      const profileUser = response?.data?.data || {};
      const nextLogo =
        profileUser?.club_details?.club_image ||
        profileUser?.club?.club_image ||
        profileUser?.club_image ||
        selectedGroup?.groupDetails?.club_image ||
        selectedGroup?.groupDetails?.club?.club_image ||
        null;

      if (nextLogo) {
        const resolved = resolveClubImageUrl(nextLogo) || defaultAppLogo;
        setResolvedLogo((prev) => (prev === resolved ? prev : resolved));
        const mergedUser = mergeProfilePreservingActiveClub(user, profileUser);
        localStorage.setItem("user", JSON.stringify(mergedUser));
        return true;
      }
      setResolvedLogo((prev) => (prev === defaultAppLogo ? prev : defaultAppLogo));
      return false;
    } catch (_error) {
      setResolvedLogo((prev) => (prev === defaultAppLogo ? prev : defaultAppLogo));
      return false;
    }
  };

  useEffect(() => {
    if (resolvedLogoFromUser) {
      const next = resolveClubImageUrl(resolvedLogoFromUser) || defaultAppLogo;
      setResolvedLogo((prev) => (prev === next ? prev : next));
      return undefined;
    }

    if (fetchedLogoRef.current) return undefined;
    fetchedLogoRef.current = true;
    let cancelled = false;
    (async () => {
      if (!cancelled) await refreshLogoFromProfile();
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedLogoFromUser]);

  const activeHomeRole = props.viewAsRole || readActiveHomeRole(user);
  const notifCount = getUserNotificationCount(user, props.notificationCount);
  const showNotificationBell = notifCount > 0;
  const showClubLogo = !props.hideClubLogo;
  const showRoleBadge = !props.hideRoleBadge;
  const showBrandStack = showClubLogo || showRoleBadge;

  return (
    <div className={`header${props.className ? ` ${props.className}` : ""}`.trim()}>
      {props.element}
      <div className="header-trailing">
        {showNotificationBell && (
          <button
            type="button"
            className="notification-btn"
            title={t("notifications")}
            style={{ position: "relative" }}
          >
            <img src={noti_svg} alt="" />
            <span className="notification-badge" />
          </button>
        )}
        {props.trailing}
        {showBrandStack ? (
          <div className="header-brand-stack">
            {showRoleBadge ? (
              <HeaderRoleBadge user={user} viewAsRole={activeHomeRole} />
            ) : null}
            {showClubLogo ? (
              <img
                src={resolvedLogo || defaultAppLogo}
                className="clublogo"
                alt=""
                onClick={() => {
                  const u = readUser();
                  const isStaff =
                    Number(u.type_user_id) === 1 ||
                    Number(u.type_user_id) === 2 ||
                    Number(u.type_user_id) === 3 ||
                    Number(u.type_user_id) === 6 ||
                    u.hasAdminRole ||
                    u.hasCommitteeRole ||
                    u.hasCoachRole ||
                    u.hasModeratorRole;
                  if (isStaff) {
                    openSwitchClubDialog();
                  }
                }}
                title={t("switchClubPrompt", "Switch club")}
                style={{ cursor: "pointer" }}
                onError={(e) => {
                  const img = e.currentTarget;
                  if (!img || img.src === defaultAppLogo) return;
                  refreshLogoFromProfile().then((ok) => {
                    if (!ok && img && img.isConnected) {
                      img.src = defaultAppLogo;
                      setResolvedLogo(defaultAppLogo);
                    }
                  });
                }}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );
};
